/**
 * OnboardingTourGate — KAN-3057 Phase 3
 * =====================================
 *
 * 7-step driver.js tour for first-run users. Mounted globally inside
 * <AppRoutes/>; fires once per user when:
 *   - PostHog flag `onboarding_tour_v1` is enabled for this user
 *   - Phase 2 timestamps are all NULL on public.users (fresh signup)
 *   - UsernamePromptGate has cleared (username_set_explicitly = true)
 *   - User hasn't deferred this session
 *
 * Replay path: `replayTour()` (called from Profile · Settings) clears
 * the 3 timestamps + sessionStorage deferral + navigates to `/` so the
 * gate re-fires.
 *
 * Telemetry is wired through `useTourTelemetry` (KAN-3056 Phase 2).
 * Persistence uses direct supabase-js UPDATEs on public.users; the user
 * owns their own row via RLS so no new RPC is needed.
 *
 * Decisions locked by Will (Confluence 2490809, 2026-05-07):
 *   - Library: driver.js (NOT framer-motion — KAN-4783 superseded).
 *   - Uniform 7 steps across tiers. Tier-tilted copy folded into Step 3
 *     and Step 6 strings.
 *   - Trigger gate sequence: signup → email-confirm → username-prompt →
 *     tour-gate (this).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

import { useAuth } from '../../contexts/SupabaseAuthContext';
import { useOnboardingTourV1 } from '../../lib/featureFlags';
import { useTourTelemetry } from '../../hooks/useTourTelemetry';
import { supabase } from '../../lib/supabase';
import {
  type TourBreakpoint,
  type TourEntryPoint,
} from '../../lib/tourEvents';

const SESSION_DEFER_KEY = 'aightbet:onboarding-tour-deferred';
const REPLAY_REQUESTED_KEY = 'aightbet:onboarding-tour-replay';

/* -------------------------------------------------------------------------- */
/*  Step config                                                                */
/* -------------------------------------------------------------------------- */

interface TourStepConfig {
  /** Stable identifier for telemetry. */
  id: string;
  /** Route this step expects. `null` = route-agnostic (centered popover). */
  route: string | null;
  /** CSS selector for the highlight anchor. `null` = centered popover. */
  selector: string | null;
  title: string;
  body: string;
  /** Popover placement relative to anchor. driver.js auto-picks if omitted. */
  side?: 'top' | 'right' | 'bottom' | 'left' | 'over';
}

/**
 * 7-step tour copy locked by Confluence 2490809 Pass-3 (2026-05-07).
 * Tier-tilted strings are folded into Step 3 (deep-dive) + Step 6 (tier).
 * Copy intentionally uses "AIghtBet Sports" customer-facing per the brand
 * sweep convention. The "AI" prefix of the brand name is wrapped in an
 * inline-styled span (KAN-25020 P1-1 brand-wordmark fix) so it pops as
 * a wordmark inside driver.js popovers, which render description/title
 * as HTML and don't accept React children.
 */
const BRAND_INLINE_HTML =
  '<span style="white-space:nowrap;"><span style="color:#FB923C;font-weight:800;">AI</span>ghtBet Sports</span>';

const TOUR_STEPS: TourStepConfig[] = [
  {
    id: 'welcome',
    route: null,
    selector: null,
    title: `Welcome to ${BRAND_INLINE_HTML}`,
    body:
      "Take a 60-second tour to see how AI predictions land on your screen, what the deep-dive reasoning looks like, and where to find your tracked predictions. You can skip any time.",
  },
  {
    id: 'hub_filter_row',
    route: '/',
    selector: '[data-tour-anchor="hub-filter-row"]',
    title: 'Filter your feed',
    body:
      'Games show in chronological order by start time. Use the chip row to filter by sport, status (All / Live / Today), and the prediction types you want to see.',
    side: 'bottom',
  },
  {
    id: 'pick_deep_dive',
    route: '/',
    // FullAnalysisPanel only mounts inside an unlocked GameCard. Fresh
    // users have 0 unlocks → fall back to centered popover if absent.
    selector: '[data-tour-anchor="full-analysis-panel"]',
    title: 'Deep-dive reasoning',
    body:
      "Unlock any prediction to reveal the AI's confidence, EV, factor breakdown, and live odds. Free unlocks 10 predictions/month — Degenerate is unlimited.",
    side: 'top',
  },
  {
    id: 'track',
    route: '/track',
    selector: '[data-tour-anchor="track-filter-chips"]',
    title: 'Track what you bet',
    body:
      'Predictions you unlock get tracked here. Filter chips: All / Live / Open / Won / Lost / Pushed. Stat strip on top: Record · Units · ROI.',
    side: 'bottom',
  },
  {
    id: 'performance',
    route: '/performance',
    selector: '[data-tour-anchor="time-window-chips"]',
    title: 'Performance, honest',
    body:
      'ROI and win rate across 7d / 30d / Season / All-time (default 30d). Every prediction is tracked — no cherry-picking. Upcoming and Analysis tabs are Degenerate-only.',
    side: 'bottom',
  },
  {
    id: 'profile_tier',
    route: '/profile',
    selector: '[data-tour-anchor="profile-subscription"]',
    title: 'Your tier',
    body:
      'Free gives you 10 predictions/month. Degenerate is unlimited. You can retake this tour any time from Settings → Subscription.',
    side: 'bottom',
  },
  {
    id: 'closing',
    route: '/',
    selector: null,
    title: "You're set",
    body:
      "Try unlocking your first prediction on Home — it'll show up in Track once you do.",
  },
];

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function getBreakpoint(): TourBreakpoint {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

/** Wait for a selector to appear in the DOM. Returns null on timeout. */
function waitForAnchor(
  selector: string,
  timeoutMs = 2500,
): Promise<HTMLElement | null> {
  if (typeof document === 'undefined') return Promise.resolve(null);
  const immediate = document.querySelector<HTMLElement>(selector);
  if (immediate) return Promise.resolve(immediate);

  return new Promise((resolve) => {
    let resolved = false;
    const finish = (el: HTMLElement | null) => {
      if (resolved) return;
      resolved = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(el);
    };
    const observer = new MutationObserver(() => {
      const found = document.querySelector<HTMLElement>(selector);
      if (found) finish(found);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timer = setTimeout(() => finish(null), timeoutMs);
  });
}

/* -------------------------------------------------------------------------- */
/*  Replay helper (called from Profile)                                       */
/* -------------------------------------------------------------------------- */

/**
 * Reset the 3 tour timestamps + clear session deferral + flag a replay
 * intent so the gate fires on the next render even if a stale user
 * snapshot from React Query is still in scope.
 *
 * Returns `{ ok: boolean }`. Caller is responsible for `refreshUser()`
 * and navigation back to `/`.
 */
export async function replayTour(authUserId: string): Promise<{ ok: boolean }> {
  try {
    sessionStorage.removeItem(SESSION_DEFER_KEY);
    sessionStorage.setItem(REPLAY_REQUESTED_KEY, '1');
  } catch {
    /* SSR / restricted storage — non-fatal */
  }
  const { error } = await supabase
    .from('users')
    .update({
      tour_started_at: null,
      tour_completed_at: null,
      tour_skipped_at: null,
    })
    .eq('auth_user_id', authUserId);
  if (error) {
    console.error('[OnboardingTour] replay reset failed', error);
    return { ok: false };
  }
  return { ok: true };
}

/* -------------------------------------------------------------------------- */
/*  Gate component                                                             */
/* -------------------------------------------------------------------------- */

export const OnboardingTourGate: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const flagOn = useOnboardingTourV1();
  const navigate = useNavigate();
  const location = useLocation();
  const telemetry = useTourTelemetry();

  // Session deferral lets a skipped tour stay skipped across re-renders
  // even before the DB write lands.
  const [sessionDeferred, setSessionDeferred] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SESSION_DEFER_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Track whether a tour is currently active to avoid double-driving.
  const [tourActive, setTourActive] = useState(false);
  const driverRef = useRef<Driver | null>(null);
  const stepStartTimeRef = useRef<number>(0);
  const tourStartTimeRef = useRef<number>(0);
  const entryPointRef = useRef<TourEntryPoint>('auto_first_login');

  // Reset deferral on signout.
  useEffect(() => {
    if (!user) {
      try {
        sessionStorage.removeItem(SESSION_DEFER_KEY);
        sessionStorage.removeItem(REPLAY_REQUESTED_KEY);
      } catch {
        /* non-fatal */
      }
      setSessionDeferred(false);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishWithTimestamp = useCallback(
    async (column: 'tour_completed_at' | 'tour_skipped_at') => {
      if (!user?.auth_user_id) return;
      try {
        await supabase
          .from('users')
          .update({ [column]: new Date().toISOString() })
          .eq('auth_user_id', user.auth_user_id);
      } catch (e) {
        console.error('[OnboardingTour] persist', column, 'failed', e);
      }
      try {
        await refreshUser();
      } catch {
        /* non-fatal */
      }
    },
    [user?.auth_user_id, refreshUser],
  );

  const markStartedTimestamp = useCallback(async () => {
    if (!user?.auth_user_id) return;
    // Only stamp if not already set (replay clears it, fresh signup is NULL).
    if (user.tour_started_at) return;
    try {
      await supabase
        .from('users')
        .update({ tour_started_at: new Date().toISOString() })
        .eq('auth_user_id', user.auth_user_id);
    } catch (e) {
      console.error('[OnboardingTour] persist tour_started_at failed', e);
    }
  }, [user?.auth_user_id, user?.tour_started_at]);

  const driveStep = useCallback(
    async (idx: number, drv: Driver) => {
      const step = TOUR_STEPS[idx];
      if (!step) return;

      // Ensure we're on the right route before highlighting.
      if (step.route && location.pathname !== step.route) {
        navigate(step.route);
      }

      // For anchored steps, wait for the anchor element to mount.
      let element: HTMLElement | null = null;
      if (step.selector) {
        element = await waitForAnchor(step.selector);
      }

      stepStartTimeRef.current = Date.now();
      drv.highlight({
        element: element ?? undefined,
        popover: {
          title: step.title,
          description: step.body,
          side: step.side,
          showButtons: ['next', 'previous', 'close'],
          nextBtnText: idx === TOUR_STEPS.length - 1 ? 'Finish' : 'Next',
          prevBtnText: 'Back',
          // Hide Back on step 0.
          disableButtons: idx === 0 ? ['previous'] : [],
        },
      });
    },
    [location.pathname, navigate],
  );

  const startTour = useCallback(
    (entryPoint: TourEntryPoint) => {
      if (tourActive) return;
      setTourActive(true);
      entryPointRef.current = entryPoint;
      tourStartTimeRef.current = Date.now();

      const breakpoint = getBreakpoint();

      const drv = driver({
        showProgress: true,
        progressText: '{{current}} of {{total}}',
        animate: true,
        allowClose: true,
        // Don't let the user click-through the highlighted element during
        // the tour — keep them focused on the popover.
        disableActiveInteraction: true,
        popoverClass: 'aightbet-tour-popover',
      });
      driverRef.current = drv;

      let currentIdx = 0;

      // Manual stepping — we override default next/prev/close handlers so
      // we can navigate routes between steps without colliding with
      // driver's internal state machine.
      const advance = async (delta: number, opts: { close?: boolean } = {}) => {
        const fromIdx = currentIdx;
        const fromStep = TOUR_STEPS[fromIdx];
        const dwellMs = Date.now() - stepStartTimeRef.current;

        if (opts.close) {
          telemetry.trackTourSkipped({
            step: fromIdx,
            step_label: fromStep.id,
            dwell_ms: dwellMs,
            breakpoint,
            entry_point: entryPointRef.current,
          });
          drv.destroy();
          driverRef.current = null;
          setTourActive(false);
          try {
            sessionStorage.setItem(SESSION_DEFER_KEY, '1');
          } catch {
            /* non-fatal */
          }
          setSessionDeferred(true);
          await finishWithTimestamp('tour_skipped_at');
          return;
        }

        if (delta > 0) {
          telemetry.trackTourStepCompleted({
            step: fromIdx,
            step_label: fromStep.id,
            dwell_ms: dwellMs,
            breakpoint,
            entry_point: entryPointRef.current,
          });
        } else {
          telemetry.trackTourStepBack({
            step: fromIdx,
            step_label: fromStep.id,
            dwell_ms: dwellMs,
            breakpoint,
            entry_point: entryPointRef.current,
          });
        }

        const nextIdx = fromIdx + delta;
        if (nextIdx >= TOUR_STEPS.length) {
          // Finished.
          telemetry.trackTourCompleted({
            total_steps: TOUR_STEPS.length,
            total_time_ms: Date.now() - tourStartTimeRef.current,
            breakpoint,
            entry_point: entryPointRef.current,
          });
          drv.destroy();
          driverRef.current = null;
          setTourActive(false);
          await finishWithTimestamp('tour_completed_at');
          return;
        }
        if (nextIdx < 0) {
          // Back from step 0 — no-op.
          return;
        }
        currentIdx = nextIdx;
        await driveStep(currentIdx, drv);
      };

      drv.setConfig({
        ...drv.getConfig(),
        onNextClick: () => {
          void advance(1);
        },
        onPrevClick: () => {
          void advance(-1);
        },
        onCloseClick: () => {
          void advance(0, { close: true });
        },
        onDestroyed: () => {
          // If driver self-destroyed (overlay click, ESC) and we didn't
          // already tear down via advance(), persist the skip.
          if (driverRef.current === drv) {
            driverRef.current = null;
            setTourActive(false);
            try {
              sessionStorage.setItem(SESSION_DEFER_KEY, '1');
            } catch {
              /* non-fatal */
            }
            setSessionDeferred(true);
            void finishWithTimestamp('tour_skipped_at');
          }
        },
      });

      telemetry.trackTourStarted({
        breakpoint,
        entry_point: entryPoint,
      });
      void markStartedTimestamp();
      void driveStep(currentIdx, drv);
    },
    [
      driveStep,
      finishWithTimestamp,
      markStartedTimestamp,
      telemetry,
      tourActive,
    ],
  );

  // Gate logic — should the tour fire?
  const shouldShow =
    !!user &&
    flagOn &&
    user.tour_completed_at === null &&
    user.tour_skipped_at === null &&
    user.username_set_explicitly === true &&
    !sessionDeferred &&
    // Defer until profile is real (avoid firing on fallback users).
    !!user.auth_user_id;

  // Auto-fire when conditions land.
  useEffect(() => {
    if (!shouldShow || tourActive) return;
    let replayRequested = false;
    try {
      replayRequested = sessionStorage.getItem(REPLAY_REQUESTED_KEY) === '1';
      if (replayRequested) {
        sessionStorage.removeItem(REPLAY_REQUESTED_KEY);
      }
    } catch {
      /* non-fatal */
    }
    startTour(replayRequested ? 'profile_replay' : 'auto_first_login');
  }, [shouldShow, tourActive, startTour]);

  // Tear down on unmount.
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  }, []);

  // This component has no rendered output — driver.js owns its own DOM.
  // Brand-orange popover accent injected once via a <style> tag so we
  // don't need to add the rules to the global stylesheet.
  return (
    <style>{`
      .aightbet-tour-popover.driver-popover {
        --driver-popover-bg: #1f1f1f;
        --driver-popover-text: #ffffff;
        --driver-popover-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
        background-color: var(--driver-popover-bg);
        color: var(--driver-popover-text);
        box-shadow: var(--driver-popover-shadow);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
      }
      .aightbet-tour-popover .driver-popover-title {
        color: #ffffff;
        font-size: 16px;
        font-weight: 600;
      }
      .aightbet-tour-popover .driver-popover-description {
        color: rgba(255, 255, 255, 0.75);
        font-size: 13px;
        line-height: 1.45;
      }
      .aightbet-tour-popover .driver-popover-progress-text {
        color: rgba(255, 255, 255, 0.5);
        font-size: 11px;
      }
      .aightbet-tour-popover .driver-popover-next-btn {
        background: #f57f24;
        color: #0b0b0b;
        text-shadow: none;
        border: 0;
        font-weight: 600;
      }
      .aightbet-tour-popover .driver-popover-next-btn:hover {
        background: #ff9447;
      }
      .aightbet-tour-popover .driver-popover-prev-btn {
        background: rgba(255, 255, 255, 0.08);
        color: #ffffff;
        border: 1px solid rgba(255, 255, 255, 0.12);
        text-shadow: none;
      }
      .aightbet-tour-popover .driver-popover-close-btn {
        color: rgba(255, 255, 255, 0.6);
      }
      .aightbet-tour-popover .driver-popover-arrow {
        border-top-color: #1f1f1f;
        border-bottom-color: #1f1f1f;
        border-left-color: #1f1f1f;
        border-right-color: #1f1f1f;
      }
    `}</style>
  );
};

export default OnboardingTourGate;
